import { createContext, ReactNode, useState, useContext } from "react";
import { UnitMeasurement } from "./model/poster-project";
import { Element as ProjectElement } from "./model/poster-types";

export interface EditorUIState {
  selectedElement: ProjectElement | undefined; //The item is selected, but not necessary being edited
  editingElement: ProjectElement | undefined;
  unitMeasurement: UnitMeasurement;
  mapSizeInPX?: { width: number; height: number };
  mapZoomInCounter: number;
  mapZoomOutCounter: number;
}

interface EditorUIContextType {
  editorUIState: EditorUIState;
  setSelectedElement: (element: ProjectElement | undefined) => void;
  setEditingElement: (element: ProjectElement | undefined) => void;
  setUnitMeasurement: (system: UnitMeasurement) => void;
  setMapSizeInPX: (size: { width: number; height: number }) => void;
  zoomMapIn: () => void;
  zoomMapOut: () => void;
  setSnapshotFunction: (snapshotFunction: () => void) => void;
  takeSnapshot: () => void;
}

const EditorUIContext = createContext<EditorUIContextType | undefined>(
  undefined
);

interface EditorUIProviderProps {
  children: ReactNode;
}

const EditorUIProvider = ({ children }: EditorUIProviderProps) => {
  const [uiState, setUIState] = useState<EditorUIState>({
    selectedElement: undefined,
    editingElement: undefined,
    unitMeasurement: "cm",
    mapZoomInCounter: 0,
    mapZoomOutCounter: 0,
  });
  const [snapshotFunction, setSnapshotFunction] = useState<() => void>(
    () => {}
  );

  const setSelectedElement = (element: ProjectElement | undefined) => {
    setUIState((prev) => {
      return {
        ...prev,
        selectedElement: element,
      };
    });
  };

  const setEditingElement = (element: ProjectElement | undefined) => {
    setUIState((prev) => ({
      ...prev,
      editingElement: element ? { ...element } : undefined, // Force a new reference
    }));
  };

  const setUnitMeasurement = (system: UnitMeasurement) => {
    setUIState((prev) => ({
      ...prev,
      unitMeasurement: system,
    }));
  };

  const setMapSizeInPX = (size: { width: number; height: number }) => {
    setUIState((prev) => ({
      ...prev,
      mapSizeInPX: size,
    }));
  };

  const zoomMapIn = () => {
    setUIState((prev) => ({
      ...prev,
      mapZoomInCounter: prev.mapZoomInCounter + 1,
    }));
  };

  const zoomMapOut = () => {
    setUIState((prev) => ({
      ...prev,
      mapZoomOutCounter: prev.mapZoomOutCounter + 1,
    }));
  };

  const takeSnapshot = () => {
    snapshotFunction();
  };

  return (
    <EditorUIContext.Provider
      value={{
        editorUIState: uiState,
        setSelectedElement,
        setEditingElement,
        setUnitMeasurement,
        setMapSizeInPX,
        zoomMapIn,
        zoomMapOut,
        setSnapshotFunction: (snapshotFunction: () => void) => {
          setSnapshotFunction(() => snapshotFunction);
        },
        takeSnapshot,
      }}
    >
      {children}
    </EditorUIContext.Provider>
  );
};

function useEditorUI() {
  const context = useContext(EditorUIContext);
  if (!context) {
    throw new Error("use-editor-ui must be used within an EditorUIProvider");
  }
  return context;
}
export { EditorUIProvider, useEditorUI };
